/**
 * HOWIE-branded "Sign in with Google".
 *
 * Supabase's Google provider carries one OAuth client for the whole project, and that client
 * belongs to Alexandria's Google Cloud project, so `signInWithOAuth` shows Alexandria's name on
 * the consent screen. To show HOWIE's, the web app asks Google for an ID token itself, with the
 * HOWIE web OAuth client (the same one the Companion extension uses), and hands that token to
 * Supabase with `signInWithIdToken`. Supabase accepts it because the HOWIE client id is listed
 * among the provider's authorized client ids (see DOCS/howie/GOOGLE_SIGN_IN_BRANDING.md).
 *
 * Flow: begin() → full-page redirect to accounts.google.com → Google redirects back to
 * /auth/google/callback with `#id_token=…&state=…` → complete() verifies the state, exchanges the
 * token with Supabase, and returns where to go next. The nonce Google receives is the SHA-256 of
 * the one Supabase verifies, as Supabase requires.
 */

export const GOOGLE_CALLBACK_PATH = '/auth/google/callback';
const STORAGE_KEY = 'howie.googleSignIn';
const GOOGLE_AUTH_URL = 'https://accounts.google.com/o/oauth2/v2/auth';

export const GOOGLE_WEB_CLIENT_ID: string = import.meta.env.VITE_GOOGLE_WEB_OAUTH_CLIENT_ID || '';

/** True when the build carries the HOWIE web client id; otherwise the Supabase redirect is used. */
export function isBrandedGoogleSignInAvailable(clientId: string = GOOGLE_WEB_CLIENT_ID): boolean {
  return clientId.trim().length > 0;
}

interface PendingSignIn {
  state: string;
  nonce: string;
  postAuthPath: string;
}

interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

export function randomToken(bytes = 16): string {
  const buffer = new Uint8Array(bytes);
  crypto.getRandomValues(buffer);
  return Array.from(buffer, (byte) => byte.toString(16).padStart(2, '0')).join('');
}

export async function sha256Hex(text: string): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(text));
  return Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, '0')).join('');
}

/** Build Google's authorization URL and remember the state and nonce for the callback. */
export async function beginGoogleSignIn(options: {
  origin: string;
  postAuthPath: string;
  clientId?: string;
  storage?: StorageLike;
  loginHint?: string;
}): Promise<string> {
  const clientId = options.clientId ?? GOOGLE_WEB_CLIENT_ID;
  if (!isBrandedGoogleSignInAvailable(clientId)) throw new Error('Google sign-in is not configured for this build.');
  const storage = options.storage ?? window.sessionStorage;
  const pending: PendingSignIn = { state: randomToken(), nonce: randomToken(), postAuthPath: options.postAuthPath };
  storage.setItem(STORAGE_KEY, JSON.stringify(pending));
  const params = new URLSearchParams({
    client_id: clientId,
    redirect_uri: `${options.origin}${GOOGLE_CALLBACK_PATH}`,
    response_type: 'id_token',
    scope: 'openid email profile',
    nonce: await sha256Hex(pending.nonce),
    state: pending.state,
    prompt: 'select_account',
  });
  if (options.loginHint) params.set('login_hint', options.loginHint);
  return `${GOOGLE_AUTH_URL}?${params.toString()}`;
}

export interface GoogleCallback {
  idToken?: string;
  state?: string;
  error?: string;
}

/** Google answers in the URL fragment: `#id_token=…&state=…` or `#error=access_denied&state=…`. */
export function parseGoogleCallback(hash: string): GoogleCallback {
  const params = new URLSearchParams(hash.replace(/^#/, ''));
  return {
    idToken: params.get('id_token') || undefined,
    state: params.get('state') || undefined,
    error: params.get('error') || undefined,
  };
}

export interface IdTokenSignIn {
  (credentials: { provider: 'google'; token: string; nonce: string }): Promise<{ error: { message: string } | null }>;
}

/**
 * Finish the sign-in on the callback page. Returns the path to continue to.
 * Throws with a message fit for the user when the state does not match, Google refused, or
 * Supabase rejected the token.
 */
export async function completeGoogleSignIn(options: {
  hash: string;
  signInWithIdToken: IdTokenSignIn;
  storage?: StorageLike;
}): Promise<string> {
  const storage = options.storage ?? window.sessionStorage;
  const raw = storage.getItem(STORAGE_KEY);
  storage.removeItem(STORAGE_KEY);
  const callback = parseGoogleCallback(options.hash);
  if (callback.error === 'access_denied') throw new Error('Google sign-in was cancelled.');
  if (callback.error) throw new Error(`Google could not sign you in (${callback.error}).`);
  if (!raw) throw new Error('This sign-in link has expired. Please try again.');
  let pending: PendingSignIn;
  try {
    pending = JSON.parse(raw) as PendingSignIn;
  } catch {
    throw new Error('This sign-in link has expired. Please try again.');
  }
  if (!callback.idToken || !callback.state || callback.state !== pending.state) {
    throw new Error('This sign-in link did not match the one we started. Please try again.');
  }
  const { error } = await options.signInWithIdToken({ provider: 'google', token: callback.idToken, nonce: pending.nonce });
  if (error) throw new Error(error.message || 'Google sign-in failed.');
  return pending.postAuthPath || '/chat';
}
